/**
 * Main Fehlzeiten Query Functions
 *
 * Primary query for the Fehlzeiten feature with pagination and filtering.
 * @module fehlzeiten/services/queries/fehlzeitenQueries
 */

import { supabase } from "../../../../../lib/supabase";
import type {
  FetchFehlzeitenParams,
  FetchFehlzeitenResult,
} from "../../types/services";
import { mapRpcRowToFehlzeitEntry } from "../helpers";

/**
 * Fetches absences using fn_get_fehlzeiten_tab_data_v1 RPC with pagination and filtering.
 * school_id and profile_id are derived server-side from auth context (gold standard).
 */
export async function fetchFehlzeitenUnified(
  params: FetchFehlzeitenParams,
): Promise<FetchFehlzeitenResult> {
  const {
    schoolId,
    page,
    pageSize,
    sortBy = "date",
    sortOrder = "desc",
    classId,
    status,
    text,
    date,
    dateFrom,
    dateTo,
    collapseMultiday = true,
    absenceNoteId,
  } = params;

  const rpcParams: Record<string, unknown> = {
    p_school_id: schoolId, // kept for backwards compat, ignored by RPC
    p_page: page,
    p_page_size: pageSize,
    p_sort_by: sortBy,
    p_sort_order: sortOrder,
    p_class_id: classId || null,
    p_status: status === "alle" ? null : status,
    p_text: text || null,
    p_date: date || null,
    p_date_from: dateFrom || null,
    p_date_to: dateTo || null,
  };

  if (collapseMultiday === false) {
    rpcParams.p_collapse_multiday = false;
  }
  if (absenceNoteId) {
    rpcParams.p_absence_note_id = absenceNoteId;
  }

  const { data, error } = await supabase.rpc(
    "fn_get_fehlzeiten_tab_data_v1" as never,
    rpcParams as never,
  );

  if (error) {
    throw new Error(`Failed to fetch absences: ${error.message}`);
  }

  const payload = data as any;
  const items = (payload?.rows || []).map((row: Record<string, unknown>) =>
    mapRpcRowToFehlzeitEntry(row),
  );

  return {
    items,
    total: payload?.metadata?.total_count || 0,
    kbStudentCount: payload?.metadata?.kb_student_count || 0,
    distinctClasses: payload?.metadata?.distinct_classes || [],
    allowedClassIds: payload?.metadata?.allowed_class_ids || [],
  };
}

/**
 * Fetches orphaned attendance logs (absences without notes or daily_log).
 */
export async function fetchOrphanedAttendanceLogs(params: {
  schoolId: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  classId?: string | null;
}): Promise<Record<string, unknown>[]> {
  const { data, error } = await supabase.rpc(
    "fn_get_orphaned_attendance_logs" as never,
    {
      p_school_id: params.schoolId,
      p_date_from: params.dateFrom || null,
      p_date_to: params.dateTo || null,
      p_class_id: params.classId || null,
    } as never,
  );

  if (error) {
    console.warn("Failed to fetch orphaned logs:", error.message);
    return [];
  }

  return Array.isArray(data) ? data : [];
}

/**
 * Fetches orphan absences for a class and date range.
 */
export async function fetchOrphanAbsences(params: {
  classId: string;
  startDate: string;
  endDate: string;
}): Promise<Record<string, unknown>[]> {
  const { data, error } = await supabase.rpc(
    "fn_get_orphan_absences" as never,
    {
      p_class_id: params.classId,
      p_start_date: params.startDate,
      p_end_date: params.endDate,
    } as never,
  );

  if (error) {
    console.warn("Failed to fetch orphan absences:", error.message);
    return [];
  }

  return Array.isArray(data) ? data : [];
}

/**
 * Fetches a single Fehlzeit record by its ID.
 */
export async function fetchFehlzeitById(
  id: string,
): Promise<Record<string, unknown> | null> {

  const { data, error } = await supabase
    .from("student_absence_notes")
    .select(
      `
      *,
      student:profile_info_student!student_id (
        id,
        first_name,
        last_name,
        main_class:structure_classes!main_class_id (
          id,
          name
        )
      ),
      creator:user_profiles!created_by (
        first_name,
        last_name
      )
    `,
    )
    .eq("id", id)
    .is("deleted_at", null)
    .single();

  if (error) {

    if (error.code === "PGRST116") return null;
    throw new Error(`Failed to fetch absence: ${error.message}`);
  }

  if (!data) return null;


  const student = data.student as Record<string, unknown> | null;
  const mainClass = student?.main_class as Record<string, unknown> | null;
  const isMultiDay = data.end_date && data.end_date !== data.start_date;

  // Resolve historical class from class_enrollments at time of absence
  let historicalClassName: string | null = null;
  if (data.student_id && data.start_date) {
    const { data: enrollment } = await supabase
      .from("class_enrollments")
      .select("class_id, structure_classes!class_id(name)")
      .eq("student_id", data.student_id)
      .lte("started_at", data.start_date)
      .or(`ended_at.is.null,ended_at.gte.${data.start_date}`)
      .order("started_at", { ascending: false })
      .limit(1)
      .single();

    if (enrollment) {
      const enrollmentClass = (enrollment as Record<string, unknown>)
        .structure_classes as Record<string, unknown> | null;
      historicalClassName = (enrollmentClass?.name as string) || null;
    }
  }

  const creator = data.creator as Record<string, unknown> | null;
  const creatorName = creator
    ? `${creator.first_name || ""} ${creator.last_name || ""}`.trim()
    : null;

  return {
    id: data.id,
    absence_note_id: data.id,
    student_id: data.student_id,
    student_name: student
      ? `${student.first_name || ""} ${student.last_name || ""}`.trim()
      : "Unbekannt",
    class_name: historicalClassName || (mainClass?.name as string) || "",
    date: data.start_date,
    start_date: data.start_date,
    end_date: data.end_date,
    is_multi: isMultiDay,
    is_multi_day: isMultiDay,
    date_range:
      data.start_time && data.end_time
        ? `${data.start_time}-${data.end_time}`
        : null,
    status: data.absence_status,
    reason: data.reason,
    has_attachment: false,
    exclude_from_zeugnis: data.exclude_from_zeugnis || false,
    source_type: data.submitted_via,
    submitted_via: data.submitted_via,
    record_date: data.start_date,
    record_date_iso: data.start_date,
    created_at: data.created_at,
    created_by: creatorName || data.created_by,
    approval_state: data.approval_state,
    approval_action: data.approval_action,
    approved_by: data.approved_by,
    approved_at: data.approved_at,
    is_excused: data.is_excused,
  };
}
